import React, {useId} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Icon} from '../Icon/Icon';
import type {IconName} from '../Icon/IconName';

interface RadioButtonProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  label?: React.ReactNode;
  subLabel?: string | React.ReactNode;
  checked?: boolean;
  iconName?: IconName;
  boxedMode?: boolean;
  fullLabelWidth?: boolean;
  style?: React.CSSProperties;
}

export const RadioButton = ({
  className,
  style,
  label,
  subLabel,
  iconName,
  boxedMode,
  fullLabelWidth,
  ref,
  ...props
}: RadioButtonProps & {ref?: React.Ref<HTMLInputElement>}) => {
  const randomId = useId();
  const radioInputId = props.id || randomId;

  return (
    <RadioLabel
      htmlFor={radioInputId}
      className={className}
      style={style}
      $withBox={boxedMode}
      $checked={props.checked}
    >
      <RadioWrapper data-full-width={fullLabelWidth || false}>
        <RadioInput {...props} type="radio" id={radioInputId} ref={ref} />
        <RadioUI />

        {iconName && (
          <IconWrapper>
            <Icon asset={iconName} size="S" />
            {label && <LabelText $withoutPadding>{label}</LabelText>}
          </IconWrapper>
        )}

        {label && !iconName && (
          <LabelText data-full-width={fullLabelWidth || false}>
            {label}
          </LabelText>
        )}
      </RadioWrapper>
      {subLabel}
    </RadioLabel>
  );
};

const RadioLabel = styled.label<{$withBox?: boolean; $checked?: boolean}>`
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  cursor: pointer;
  ${props =>
    props.$withBox
      ? css`
          border: 1px solid ${props.$checked ? COLORS.Black : COLORS.Grey3};
          padding: 1rem;
          border-radius: 4px;
        `
      : ''}
`;

const RadioWrapper = styled.div`
  display: flex;

  &[data-full-width='true'] {
    width: 100%;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
`;

const LabelText = styled.span<{$withoutPadding?: boolean}>`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 1.43;
  color: ${COLORS.Black};
  ${props => (props.$withoutPadding ? '' : 'padding-left: 15px;')}
  &[data-full-width='true'] {
    width: 100%;
  }
`;

const RadioUI = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border-radius: 50%;
  border: 2px solid ${COLORS['Fill.Grey.Secondary']};
  box-sizing: border-box;

  &::after {
    content: ' ';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${COLORS.Black};
    position: absolute;
    top: 4px;
    left: 4px;
    transition: transform 100ms;
    transform: scale(0);
  }
`;

const RadioInput = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  width: 20px;
  height: 20px;
  margin: 0;
  &:checked {
    & ~ ${LabelText} {
      color: ${COLORS.Black};
    }
    & ~ ${RadioUI} {
      border-color: ${COLORS.Black};
      border-width: 2px;

      &::after {
        transform: scale(1);
      }
    }
  }

  &:focus {
    & ~ ${RadioUI} {
      border-color: ${COLORS.Black};
      border-width: 2px;
    }
  }

  &:focus-visible {
    & ~ ${RadioUI} {
      border-color: ${COLORS.Black};
      border-width: 2px;

      &:before {
        content: '';
        position: absolute;
        inset: -4px;
        border: 2px solid ${COLORS.Black};
      }
    }
  }
`;
